import {
  Row,
  Col,
  Card,
  Flex,
  Typography,
  Radio,
  theme,
  DatePicker,
  Segmented,
  Empty,
  Spin,
} from "antd";
import { useMemo, useState, useEffect, useContext } from "react";
import EnergyConsumptionHighlightCard from "../../components/dashboard/EnergyConsumptionHighlightCard";
import MaxDemandHighlightCard from "../../components/dashboard/MaxDemandHighlightCard";
import BillingHighlightCard from "../../components/dashboard/BillingHighlightCard";
import PVGenerationHighlightCard from "../../components/dashboard/PVGenerationHighlightCard";
import PowerFactorHighlightCard from "../../components/dashboard/PowerFactorHighlightCard";
import AlarmsHighlightCard from "../../components/dashboard/AlarmsHighlightCard";
import GeneralInfoCard from "../../components/dashboard/GeneralInfoCard";
import InsightCard from "../../components/dashboard/InsightCard";
import CustomLineChart from "../../components/common/CustomLineChart";
import dayjs from "dayjs";
import { IndexContext } from "../../context/IndexContext";
import useFetch from "../../hooks/useFetch";
import "../../styles/Dashboard/DashboardPage.css";

const { Text, Title } = Typography;

function DashboardPage() {
  const { authenticationState, siteSelected, siteList } =
    useContext(IndexContext);

  const { token } = theme.useToken();

  const [fetchWrapper] = useFetch();
  const [loading, setLoading] = useState(false);

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(dayjs());

  // energy consumption / power demand line chart date and date type selection
  const [lineChartDateSelection, setLineChartDateSelection] = useState(dayjs());
  const lineChartDateTypeOptions = [
    { label: "Day", value: "date" },
    { label: "Month", value: "month" },
    { label: "Year", value: "year" },
  ];
  const [lineChartDateTypeSelection, setLineChartDateTypeSelection] = useState(
    lineChartDateTypeOptions[0].value
  );

  // energy consumption / power demand line chart type selection
  const lineChartTypeOptions = [
    { label: "Energy Consumption", value: "energy" },
    { label: "Power Demand", value: "power_demand" },
  ];
  const [lineChartTypeSelection, setLineChartTypeSelection] = useState(
    lineChartTypeOptions[0].value
  );

  // energy consumption / power demand chart data
  const [powerEnergyChartData, setPowerEnergyChartData] = useState([]);

  //for highlights
  const [highlightData, setHighlightData] = useState({
    energy_current: null,
    energy_last: null,
    md_current: null,
    md_last: null,
    pv_generation_current: null,
    pv_generation_last: null,
    pf_current: null,
    pf_last: null,
    alarm_count_current: null,
    alarm_count_last: null,
  });

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "trending/getSiteCommissioningDate",
      body: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setSiteCommissioningDate(fetch_output.site_commissioning_date);
        }
      },
    });
  }

  /* API to get highlight data */
  function getHighlightData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getHighlightData",
      body: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        setLoading(false);
        if (fetch_output.status === true) {
          setHighlightData(fetch_output.highlight_data);
        }
      },
    });
  }

  /* API to get energy consumption / power demand chart data */
  function getPowerEnergyChartData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getPowerEnergyChartData",
      body: {
        site_id: siteSelected,
        date_type: lineChartDateTypeSelection,
        date_selected: lineChartDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        chart_type: lineChartTypeSelection,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setPowerEnergyChartData(fetch_output.power_energy_chart_data);
        }
      },
      onFinish: () => {
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getHighlightData();

      const timer = setInterval(() => {
        getHighlightData();
      }, 10 * 60 * 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [siteSelected]);

  useEffect(() => {
    if (siteSelected) {
      getPowerEnergyChartData();
      getSiteCommissioningDate();
    }
  }, [
    siteSelected,
    lineChartDateTypeSelection,
    lineChartDateSelection,
    lineChartTypeSelection,
  ]);

  return (
    <>
      {siteList.length !== 0 ? (
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <Flex justify="space-between" align="center">
              <Text style={{ margin: 0 }} strong>
                HIGHLIGHTS OF {dayjs().format("MMM YYYY").toUpperCase()}
              </Text>
              {/* <Radio.Group defaultValue="Monthly" buttonStyle="solid">
                <Radio.Button value="Daily">Daily</Radio.Button>
                <Radio.Button value="Monthly">Monthly</Radio.Button>
                <Radio.Button value="Yearly">Yearly</Radio.Button>
              </Radio.Group> */}
            </Flex>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} md={12} lg={12} xl={8} xxl={{ flex: "20%" }}>
                <EnergyConsumptionHighlightCard
                  currentValue={highlightData.energy_current}
                  lastValue={highlightData.energy_last_month}
                />
              </Col>
              <Col xs={24} sm={12} md={12} lg={12} xl={8} xxl={{ flex: "20%" }}>
                <MaxDemandHighlightCard
                  currentValue={highlightData.md_current}
                  currentValueDateTime={highlightData.md_current_datetime}
                  lastValue={highlightData.md_last}
                />
              </Col>
              <Col xs={24} sm={12} md={12} lg={12} xl={8} xxl={{ flex: "20%" }}>
                <BillingHighlightCard
                  currentValue={highlightData.total_estimated_billing}
                  lastValue={highlightData.last_total_estimated_billing}
                  // lastValue={
                  //   highlightData.energy_last * 36.5 +
                  //   highlightData.md_last * 30.3
                  // }
                />
              </Col>
              <Col xs={24} sm={12} md={12} lg={12} xl={8} xxl={{ flex: "20%" }}>
                <PVGenerationHighlightCard
                  currentValue={highlightData.pv_generation_current}
                  lastValue={highlightData.pv_generation_last}
                />
              </Col>
              <Col xs={24} sm={12} md={12} lg={12} xl={8} xxl={{ flex: "20%" }}>
                <PowerFactorHighlightCard
                  currentValue={highlightData.pf_current}
                  lastValue={highlightData.pf_last}
                />
              </Col>
              {/* <Col xs={24} sm={12} md={12} lg={12} xl={8} xxl={4}>
                <AlarmsHighlightCard
                  currentValue={highlightData.alarm_count_current}
                  // lastValue={highlightData.alarm_count_last}
                />
              </Col> */}
            </Row>
          </Col>
          <Col xs={24} sm={24} md={24} lg={9} xl={9} xxl={7}>
            <GeneralInfoCard SiteID={siteSelected} />
          </Col>
          <Col xs={24} sm={24} md={24} lg={15} xl={15} xxl={10}>
            <Card
              styles={{ body: { height: "100%", minHeight: 520 } }}
              title={
                <Flex
                  justify="space-between"
                  align="center"
                  wrap={true}
                  gap={8}
                >
                  <Segmented
                    options={lineChartTypeOptions}
                    onChange={(value) => {
                      setLineChartTypeSelection(value);
                    }}
                    value={lineChartTypeSelection}
                  />

                  <Flex gap={8} align="center">
                    <DatePicker
                      allowClear={false}
                      value={lineChartDateSelection}
                      picker={lineChartDateTypeSelection}
                      onChange={(date, _) => {
                        setLineChartDateSelection(date);
                      }}
                      minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
                      maxDate={dayjs()}
                    />
                    <Segmented
                      options={lineChartDateTypeOptions}
                      onChange={(value) => {
                        setLineChartDateTypeSelection(value);
                      }}
                      value={lineChartDateTypeSelection}
                    />
                  </Flex>
                </Flex>
              }
              size="small"
              className="h-100 power-energy-chart"
            >
              <Spin spinning={loading} size="large">
                {powerEnergyChartData.length !== 0 ? (
                  <CustomLineChart
                    height={520}
                    tooltipValueFormatter={(value, unit) => {
                      if (value == null || value == undefined) return "-";
                      if (value >= 1000000)
                        return `${(value / 1000000)?.toLocaleString({
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })} ${
                          lineChartTypeSelection == "energy" ? "GWh" : "GW"
                        }`;
                      else if (value >= 1000)
                        return `${(value / 1000)?.toLocaleString({
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })} ${
                          lineChartTypeSelection == "energy" ? "MWh" : "MW"
                        }`;
                      else
                        return `${value?.toLocaleString({
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })} ${
                          lineChartTypeSelection == "energy" ? "kWh" : "kW"
                        }`;
                    }}
                    data={powerEnergyChartData}
                    themeToken={token}
                  />
                ) : (
                  <Empty />
                )}
              </Spin>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={7}>
            <InsightCard themeToken={token} SiteID={siteSelected} />
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={8} xxl={8}></Col>
        </Row>
      ) : (
        <div> There is no site </div>
      )}
    </>
  );
}

export default DashboardPage;
